import { useEffect, useRef, useState } from "react";
import { Send, Paperclip, Loader2, FileText, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { uploadFile, errMsg, fileUrl } from "@/lib/api";
import { ftime } from "@/lib/format";
import { cn } from "@/lib/utils";

function Attachment({ url }) {
  if (!url) return null;
  const src = fileUrl(url);
  return /\.(png|jpe?g|gif|webp)$/i.test(url)
    ? <img src={src} alt="" className="mt-2 rounded-lg max-h-56 object-cover" />
    : <a href={src} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-1.5 text-xs underline"><FileText className="h-3.5 w-3.5" />Attachment</a>;
}

export function ChatView({ messages, mineRole, onSend, theirName, closed }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();
  const fileRef = useRef();
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() && !file) return;
    setBusy(true);
    try {
      await onSend({ body: text, attachment: file });
      setText(""); setFile("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { setFile((await uploadFile(f)).url); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="h-full grid place-items-center text-center text-muted-foreground" data-testid="chat-empty">
            <div><MessageCircle className="h-8 w-8 mx-auto mb-3 text-primary/60" strokeWidth={1.4} /><p className="text-sm">No messages yet.</p></div>
          </div>
        ) : messages.map((m) => {
          const mine = m.sender_role === mineRole;
          return (
            <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")} data-testid={`chat-message-${m.id}`}>
              <div className={cn("max-w-[80%] rounded-2xl px-4 py-2.5 text-sm", mine ? "gold-bg text-black rounded-br-md" : "bg-secondary rounded-bl-md")}>
                {!mine && <p className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-0.5">{theirName}</p>}
                {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
                <Attachment url={m.attachment} />
                <p className={cn("text-[10px] mt-1", mine ? "text-black/60" : "text-muted-foreground")}>{ftime(m.created_at)}{mine && (m.read ? " · Read" : " · Sent")}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="border-t border-border p-3 sm:p-4">
        {closed && <p className="text-[11px] text-muted-foreground mb-2">This conversation was closed. Sending a message will reopen it.</p>}
        {file && <p className="text-xs text-primary mb-2" data-testid="chat-attachment-ready">File attached · <button className="underline" onClick={() => setFile("")}>remove</button></p>}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid="chat-file-input" />
          <Button variant="outline" size="icon" className="rounded-full shrink-0" onClick={() => fileRef.current.click()} disabled={busy} data-testid="chat-attach-button"><Paperclip className="h-4 w-4" /></Button>
          <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message…"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="min-h-[44px] max-h-32 rounded-2xl bg-background resize-none" data-testid="chat-input" />
          <Button size="icon" className="rounded-full shrink-0 h-11 w-11" onClick={send} disabled={busy} data-testid="chat-send-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
